/**
 * Registre des « règles de mapping » documentées par lot — les correspondances
 * V1 → V2 réellement appliquées par les loaders côté backend. Purement
 * informatif (affiché dans `MappingRulesDialog` via un bouton sur la ligne du
 * lot dans `LotsTable`).
 *
 * À garder synchrone avec les loaders (`src/Migration/Service/Runner/Loader/`)
 * et les `sourceQuery`/`fieldMapping` de `SeedStepsCommand`. Le bouton « Règles
 * de mapping » n'apparaît que pour les lots présents dans ce registre.
 */
export interface MappingRule {
  /** Champ cible côté V2 (libellé lisible). */
  field: string;
  /** Source V1 ou règle de dérivation. */
  source: string;
  /** Précision optionnelle (cas limites, best-effort, héritage…). */
  note?: string;
}

export interface LotMappingRules {
  /** Phrase d'introduction optionnelle affichée en tête du dialog. */
  intro?: string;
  rules: MappingRule[];
}

export const LOT_MAPPING_RULES: Record<string, LotMappingRules> = {
  'settings.establishments': {
    intro:
      "Établissements V1 (table « etablissement », champs métier dans le JSON « options ») vers la table tenant V2 « establishment ». Si le mode multi-établissement V1 est actif (setting systeme_etablissment = 1), chaque établissement V1 est migré. Sinon (école mono-établissement), un record unique « Réglages généraux » porte les infos école (nom, adresse, RNE, académie + IBAN/BIC/ICS des réglages SEPA) vers l'établissement par défaut du tenant — en V2 le premier établissement est de facto l'école.",
    rules: [
      {
        field: 'Correspondance',
        source: 'RNE, sinon nom (insensible à la casse)',
        note: "Sinon le premier établissement V1 complète l'« Établissement principal » créé au provisioning du tenant ; sinon création (UUID déterministe + settings par défaut). Le record « Réglages généraux » ne crée jamais d'établissement : à défaut de match, il complète l'établissement par défaut du tenant.",
      },
      {
        field: 'Repli mono-établissement (record « reglages-generaux »)',
        source: 'Réglages généraux V1 : sname / address / rne / academie + ini_config : sepa_iban / sepa_bic / sepa_ics',
        note: "Appliqué quand le multi-établissement V1 est inactif ou sans établissement : l'école elle-même est l'établissement.",
      },
      {
        field: 'Nom',
        source: 'etablissement.name',
      },
      {
        field: 'RNE',
        source: "options.rne ('0' = sans RNE → ignoré)",
        note: 'Posé uniquement si l’établissement V2 n’en a pas (immuable côté V2) ; conflit loggé, valeur V2 conservée.',
      },
      {
        field: 'Académie (code + libellé)',
        source: 'RNE → résolution V2 (data.education.gouv), sinon options.academie + libellé api data',
        note: 'Best-effort.',
      },
      {
        field: 'Type de contrat',
        source: 'options.school_type, sinon dérivé du type d’école ADM',
        note: 'private_out_of_contract → hors contrat ; public / private_under_contract → sous contrat.',
      },
      {
        field: 'Adresse postale',
        source: 'options.addresse',
        note: 'Nettoyée (HTML, espaces invisibles, retours à la ligne).',
      },
      {
        field: 'IBAN / BIC / ICS',
        source: 'options.iban / options.bic / options.ics',
        note: 'Colonnes V2 de l’établissement ; mise à jour non destructive (les vides V1 n’écrasent rien).',
      },
      {
        field: 'Formats de numérotation, footer, adresse de facturation, logo, groupes',
        source: 'options.invoice_format / avoir_format / format_auxieliere* / footer / adresse_facturation / file_name, liaisons groupes',
        note: "Pas de cible V2 par établissement : conservés dans les données cibles de l'enregistrement pour le futur lot Facturation.",
      },
      {
        field: 'Liaisons sections → établissement',
        source: 'etablissment_section_relation',
        note: 'Consommées par le lot Classes pour poser l’établissement de chaque classe.',
      },
    ],
  },
  'school.levels': {
    intro:
      'Correspondances appliquées à la migration des niveaux (table V1 « classes » = le programme/grade : CP, CE1, 6ème… — les « sections » V1 sont les classes physiques, migrées par le lot Classes).',
    rules: [
      {
        field: 'Désignation',
        source: 'classes.classes (libellé V1)',
        note: "Reprise telle quelle, sans concaténation d'un libellé ni du code école.",
      },
      {
        field: 'Nom',
        source: 'Niveau normalisé (référentiel)',
        note: 'Libellé data.ecole-futee.com — override par école, sinon défaut global par libellé.',
      },
      {
        field: 'Code',
        source: 'Code du niveau normalisé (référentiel)',
      },
      {
        field: 'Cycle',
        source: 'classes.cycleID',
        note: 'Optionnel côté V2.',
      },
      {
        field: 'Capacité',
        source: 'classes.capacity',
      },
      {
        field: 'Commentaire',
        source: 'classes.note',
      },
      {
        field: 'Enseignant responsable',
        source: 'classes.teacherID → UserRole « teacher » V2',
        note: "Best-effort : ignoré si l'enseignant n'est pas (encore) migré.",
      },
      {
        field: 'Exclure des effectifs annuels',
        source: 'classes.exclude',
      },
      {
        field: "Visible sur le formulaire d'inscription",
        source: 'classes.enable_onlineadmission',
      },
    ],
  },
  'school.classes': {
    intro:
      "Correspondances appliquées à la migration des classes. En V1, la « classe » (au sens V2) EST la « section » — le niveau étant la table V1 « classes », migrée par le lot Niveaux.",
    rules: [
      {
        field: 'Nom',
        source: 'section.section (libellé V1 brut)',
      },
      {
        field: 'Niveau(x)',
        source: 'classes_sections → programme(s) parent(s) → Level(s) V2',
        note: 'Cross-step depuis le lot Niveaux ; multi-niveaux possible (ex. « 6-9 ans ») ; requis côté V2.',
      },
      {
        field: 'Enseignant principal',
        source: 'section.main_teacher → UserRole « teacher » V2',
        note: 'Requis côté V2 : la classe échoue si le prof principal V1 est absent / non migré.',
      },
      {
        field: 'Enseignants additionnels',
        source: 'sectionteacher → UserRoles « teacher » V2',
        note: 'Hors enseignant principal (dédupliqué) ; best-effort.',
      },
      {
        field: 'Capacité',
        source: 'section.capacity',
      },
      {
        field: 'Commentaire',
        source: 'section.note',
      },
      {
        field: 'Ordre',
        source: 'classes.classes_numeric (via classes_sections)',
      },
      {
        field: 'Salle',
        source: 'section.roomID → Room V2 (cross-step Salles)',
        note: 'Best-effort : souvent NULL en V1, classe créée sans salle.',
      },
      {
        field: 'Établissement',
        source: 'etablissment_section_relation → lot Établissements',
        note: "Établissement de la section V1 s'il est migré ; sinon établissement par défaut du tenant. Réconcilié au re-run (SQL direct, l'API V2 ne modifie pas l'établissement d'une classe).",
      },
    ],
  },
  'school.class_students': {
    intro:
      "Affectation des élèves à leur classe (1 ligne par élève). Additive et idempotente (on ne lie que les nouveaux, jamais de déliaison).",
    rules: [
      {
        field: 'Élève (source)',
        source: 'student × studentrelation (année source), 1 enregistrement/élève',
        note: 'Filtre actif (date_out / date_desistement NULL ou futur) — aligné sur le lot Élèves ; les sortis/désistés sont écartés. Lien classe via srclassesID → classes_sections → section (srsectionID non fiable).',
      },
      {
        field: 'Classe cible',
        source: 'Classe V2 de la section de l’élève (cross-step Classes)',
      },
      {
        field: 'Élève V2',
        source: 'UserRole « student » V2 (cross-step Élèves)',
      },
    ],
  },
  'billing.chart_of_accounts': {
    intro:
      "Plan comptable V1 (table « plancomptable » : PCG seedé + personnalisations de l'école) vers les comptes V2. Un compte au code déjà présent côté V2 (plan associatif ANC seedé, ou posé à la main) est réutilisé tel quel, jamais écrasé.",
    rules: [
      { field: 'Code', source: 'plancomptable.code', note: 'Dédupliqué (la V1 tolère des doublons de code, la V2 non — la ligne la plus ancienne gagne). Code non numérique ou > 20 chiffres → ligne exclue : la V2 le refuse.' },
      { field: 'Libellé', source: 'plancomptable.libelle' },
      { field: 'Type de compte', source: 'Classe du code (1er chiffre)', note: '1 → capitaux, 2/3 → actif, 4 → tiers, 5 → financier, 6 → charges, 7 → produits.' },
      { field: 'Compte parent', source: 'Plus long préfixe du code présent dans le plan V1', note: 'Résolu par code côté V2 (y compris sur les comptes ANC déjà en base) ; parent introuvable → compte posé à la racine, signalé.' },
      { field: 'Description', source: 'plancomptable.commentaire' },
      { field: 'Comptes auxiliaires clients (souscomptes 411…)', source: '—', note: 'Non repris : la V2 génère les siens (format des comptes clients dans les réglages Facturation V2).' },
    ],
  },
  'billing.tax_rates': {
    intro:
      "Taux de TVA V1 (table « tva » : seed de 5 taux français + personnalisations) vers les taux V2. L'unicité V2 porte sur la VALEUR du taux : un taux V2 de même valeur est réutilisé tel quel, jamais dupliqué ni réécrit.",
    rules: [
      { field: 'Taux', source: 'tva.tva_rate', note: 'Correspondance par valeur ; le taux n’est plus modifiable en V2 après création.' },
      {
        field: 'Nom',
        source: 'Composé « TVA 20% — {nom V1} » (taux 0 → « Exonéré de TVA »)',
        note: 'Le nom V1 seul (« normal », « réduit »…) serait illisible en V2, qui n’a pas de champ description. Nom et description V1 conservés dans les données cibles.',
      },
      { field: 'Taux par défaut', source: 'tva.is_default', note: 'La V2 retire le drapeau des autres taux elle-même.' },
      {
        field: 'Compte de TVA collectée',
        source: 'tva.plan_comptable_id → plancomptable.code (44571)',
        note: 'Résolu par code dans le plan du tenant (lot Plan comptable passé avant) — best-effort, omis si absent.',
      },
    ],
  },
  'billing.fiscal_years': {
    intro:
      "Exercices comptables V1 (table « financialyear ») encore ouverts (actifs ou non échus) vers les exercices V2. Les exercices passés ne sont pas repris : l'historique comptable V1 reste en V1. Lot souvent vide, c'est normal.",
    rules: [
      { field: 'Nom', source: 'financialyear.title' },
      { field: 'Dates de début / fin', source: 'startingdate / endingdate' },
      { field: 'Exercice en cours', source: 'financialyear.active' },
      {
        field: 'Correspondance',
        source: 'Nom (insensible à la casse), sinon période qui recouvre',
        note: 'La V2 refuse deux exercices qui se chevauchent : un exercice V2 recouvrant la période V1 est réutilisé.',
      },
    ],
  },
  'billing.payment_schedules': {
    intro:
      "Modèles d'échéancier V1 (« payment_schedule » : mois calendaires + jour d'échéance) vers les modèles V2 (nombre d'échéances + règle de date). Divergence de modèle assumée : la V2 ne raisonne pas en mois calendaires.",
    rules: [
      { field: 'Nom', source: 'payment_schedule.label', note: 'Correspondance par nom : un modèle V2 homonyme (seed ou créé à la main) est réutilisé, jamais dupliqué.' },
      {
        field: "Nombre d'échéances",
        source: 'Nombre de mois du CSV « months » (1 si vide, ex. « Au comptant »)',
        note: 'Plafonné à 12 (maximum V2).',
      },
      {
        field: 'Règle de date',
        source: '« X jours après facture », intervalle 30 j (0 j si échéance unique)',
        note: "La V2 n'a pas de « jour J de chaque mois » : les mois V1 et le jour d'échéance sont conservés dans la description du modèle pour ajustement par l'école.",
      },
    ],
  },
  'billing.articles': {
    intro:
      "Catalogue d'articles V1 (table « feetypes », montants HT) vers les articles V2 (Product). Le loader résout ou crée les référentiels V2 nécessaires (TVA, compte de produit, catégorie de recette). Les affectations par élève (feetypes_student) et les factures émises sont hors périmètre.",
    rules: [
      { field: 'Nom', source: 'feetypes.feetypes' },
      {
        field: 'Code (unique V2, inexistant en V1)',
        source: 'Slug majuscule du nom (ex. FRAIS-DE-SCOLARITE)',
        note: 'Repli « -V1-{id} » si le code est déjà pris — y compris par un article V2 supprimé, dont le code reste réservé.',
      },
      {
        field: 'Prix unitaire HT',
        source: 'feetypes.default_amount (NULL → 0,00)',
        note: 'Montant NÉGATIF (bourse, remise) → ligne EXCLUE : en V2 ce sont des conditions tarifaires ou des avoirs.',
      },
      {
        field: 'Code analytique / description',
        source: 'feetypes.note (« code analytique » dans l’UI V1)',
        note: '≤ 50 caractères → code analytique V2 ; plus long (texte libre) → description.',
      },
      { field: 'Actif', source: 'feetypes.status', note: 'Un article système V1 (private=1) supprimé garde delete_date NULL avec status=0 : il est filtré comme supprimé.' },
      {
        field: 'TVA',
        source: 'tva.tva_rate (via feetypes.tva_id)',
        note: 'Find-or-create du taux V2 PAR VALEUR ; taux 0 ou absent → article exonéré (sans TVA).',
      },
      {
        field: 'Compte de produit (requis V2)',
        source: 'plancomptable_categories → plancomptable.code',
        note: 'Code exact dans le plan du tenant ; sinon import du plan V2 par défaut puis création du compte manquant (parent = plus long préfixe). Sans catégorie V1 → compte 706.',
      },
      {
        field: 'Catégorie de recette',
        source: 'default_cantine → « Cantine » ; asset_family.name → catégorie par nom, sinon créée',
        note: 'Best-effort : champ optionnel V2, laissé vide en cas d’échec.',
      },
      {
        field: 'Facturation automatique, quantité, échéancier par défaut, remise par champ perso, année scolaire',
        source: 'recurring_fee / default_quantity / paymentScheduleID / discount_customfield* / schoolYearID',
        note: "Pas d'équivalent sur l'article V2 (la facturation V2 se pilote autrement) : conservés dans les données cibles de l'enregistrement.",
      },
    ],
  },
  'billing.pricing_slices': {
    intro:
      "Tranches de facturation V1 (« billing_slice » + grille « feetypes_has_billingslice ») vers les conditions tarifaires V2 (type simple, montant fixe par article, sans limite d'exercice). Les familles ne sont PAS rattachées : l'affectation par payeur est hors périmètre du catalogue.",
    rules: [
      { field: 'Nom', source: 'billing_slice.billingslice_name', note: 'Une condition V2 homonyme est réutilisée sans modification.' },
      {
        field: 'Règles par article (montant fixe)',
        source: 'feetypes_has_billingslice.amount',
        note: 'La grille V1 stocke 4 lignes par couple article×tranche (une par périodicité annuel/semestriel/trimestriel/mensuel) dont une seule porte le montant réel : c’est elle qui est migrée. Article V2 résolu via le lot Catalogue d’articles ; article exclu ou non migré → règle sautée (listée dans les données cibles).',
      },
      { field: 'Exercices', source: '— (isUnlimited)', note: 'Les tranches V1 ne sont pas datées par exercice.' },
    ],
  },
  'billing.payment_methods': {
    intro:
      'Active côté V2 les moyens de paiement en usage en V1, avec la configuration que la V2 exige à l’activation — reconstituée depuis les vraies données V1, jamais inventée. Un moyen déjà configuré côté V2 n’est jamais réécrit.',
    rules: [
      {
        field: 'Espèces',
        source: 'Toujours proposé en V1 → activé (aucune donnée requise)',
      },
      {
        field: 'Chèque',
        source: 'Ordre du chèque = nom de l’école (setting.sname, sinon nom ADM)',
      },
      {
        field: 'Virement',
        source: 'IBAN/BIC = compte de l’école (ini_config sepa_iban/sepa_bic) ; banque = 1re banque du module compta V1 (custom_banks)',
        note: 'Donnée manquante en V1 → ligne exclue avec sa raison : à renseigner dans Facturation → Réglages → Moyens de paiement V2.',
      },
      {
        field: 'Prélèvement SEPA',
        source: 'Créancier = nom de l’école + ini_config sepa_ics / sepa_iban / sepa_bic',
        note: 'Coordonnées incomplètes en V1 → ligne exclue avec sa raison.',
      },
      {
        field: 'Carte',
        source: '—',
        note: 'Toujours exclue : la V2 exige un processeur configuré (Stripe) et les identifiants marchands V1 ne sont jamais recopiés.',
      },
      {
        field: 'Retenue sur salaire, compte client, « Autre »',
        source: '—',
        note: "Pas d'équivalent V2 : non repris.",
      },
    ],
  },
  'billing.numbering': {
    intro:
      'Masques de numérotation V1 (réglages « invoice_format » / « credit_format », jetons [YYYY] [YY] [MM] [XXXX]…) vers la configuration de numérotation V2 (factures et avoirs).',
    rules: [
      { field: 'Préfixe', source: 'Littéral avant le premier jeton (ex. « FA-[YYYY]… » → FA)' },
      {
        field: 'Format de date',
        source: '[MM] → année+mois ; [YYYY]/[YY] seuls → année ; aucun → sans date',
        note: '[YY] sans [YYYY] → année courte. L’ordre des jetons V1 n’est pas reproductible : la V2 compose elle-même préfixe-date-compteur.',
      },
      { field: 'Chiffres du compteur', source: 'Longueur du jeton [XXXX] (défaut 4)' },
      { field: 'Remise à zéro par exercice', source: 'invoice_code_restart / credit_code_restart = « year »' },
      {
        field: 'Premier numéro V2',
        source: 'Plus grand compteur JAMAIS émis en V1 (relu dans les numéros réels, supprimés compris) + 1',
        note: 'La V2 numérote « max(premier numéro, dernier compteur émis + 1) » : ce plancher garantit qu’aucun numéro V2 ne réutilise un compteur V1, quel que soit le format. Le comptage V1 reste un plancher de secours (il est faux dès qu’une école a tourné sans exercice couvrant la date).',
      },
      {
        field: 'Devis',
        source: 'Masque des factures V1, préfixe « DE », compteur à 1',
        note: 'La V1 n’a pas de devis ; la V2 exige une numérotation par type pour ouvrir la facturation.',
      },
      {
        field: 'Surcharge par établissement, jetons [DD]/[E]',
        source: 'etablissement.options',
        note: "Pas d'équivalent V2 (numérotation unique par espace école) : non repris, signalés dans les données cibles.",
      },
    ],
  },
  'billing.organization': {
    intro:
      "Identité de l'organisation facturante V2 (informations générales de la facturation) depuis les réglages généraux V1. Une identité déjà saisie côté V2 n'est jamais réécrite.",
    rules: [
      { field: "Nom de l'organisation", source: 'setting.sname' },
      { field: 'Adresse', source: 'setting.address_headoffice, sinon setting.address' },
      {
        field: 'SIRET / RNA',
        source: '—',
        note: "La V1 ne les stocke pas en réglage (au mieux dans le pied de page des factures) : à compléter à la main, la V2 en exige un pour déclarer la facturation prête.",
      },
    ],
  },
  'billing.document_templates': {
    intro:
      "Gabarits PDF V2 par défaut (facture, devis, avoir) : la V2 en exige un par type et n'en seede aucun. Dessin standard V2, textes repris des réglages d'impression V1. Un gabarit par défaut déjà posé par l'école est réutilisé.",
    rules: [
      { field: 'Mentions légales', source: 'setting.invoice_footer (HTML aplati en texte)', note: 'RIB, SIRET, mention de TVA… le même texte pour les trois types, comme en V1.' },
      { field: 'Message de bas de page', source: 'setting.footer, si footer_facturation_show = 1' },
      { field: 'Nom', source: '« Modèle V1 — Facture / Devis / Avoir »' },
      { field: 'Logo, numéro (afficher_logo_facturation, afficher_numero_facturation)', source: '—', note: 'Toujours affichés par la V2 : gardés en information.' },
    ],
  },
  'billing.payers': {
    intro:
      "Payeurs V2 : un payeur FAMILLE par foyer V1 (père + mère) ayant un élève inscrit sur l'année source ou une facture non soldée ; un payeur ÉLÈVE MAJEUR ou ENTREPRISE/PARTICULIER pour les élèves et tiers qui paient eux-mêmes une facture non soldée.",
    rules: [
      {
        field: 'Famille',
        source: 'parents → payers/families-from-guardians (UserRole legal_guardian des parents migrés)',
        note: 'La V2 rattache elle-même les élèves des responsables et prend leur e-mail de compte ; l’e-mail V1 (parents.email / mail_mother) sert de secours.',
      },
      { field: 'Nom de famille', source: 'parents.name (sinon père, sinon mère)' },
      { field: 'Élève payeur', source: 'student (payeur_type = student) → payeur adult_student rattaché à son UserRole élève' },
      { field: 'Tiers', source: 'third_party → payeur company (raison sociale) ou individual, élèves de third_party_student rattachés' },
      {
        field: 'E-mail de facturation',
        source: 'parents.email / mail_mother, student.email, third_party.email',
        note: 'Requis par la V2 : sans aucun e-mail la ligne est EXCLUE avec sa raison (payeur à créer à la main).',
      },
      {
        field: 'Adresse',
        source: 'Adresse V1 (code postal / ville extraits si possible)',
        note: 'Requise par la V2 : champs manquants posés « à compléter » + avertissement.',
      },
      { field: 'Doublons', source: 'Index des payeurs V2 (responsables, élèves rattachés, e-mail)', note: 'Un payeur existant est réutilisé, jamais dupliqué (l’endpoint V2 famille n’est pas idempotent).' },
    ],
  },
  'billing.sepa_creditor': {
    intro:
      "Identité SEPA de l'école comme créancier (ICS, IBAN et BIC de remise) depuis les réglages « Paiement » V1, vers la configuration Prélèvement SEPA de la V2 — socle des mandats et des remises pain.008.",
    rules: [
      { field: 'ICS', source: 'ini_config sepa_ics', note: 'Vérifié comme en V2 (format + clé de contrôle ISO 7064) : un ICS de test invalide est refusé avec la raison.' },
      { field: 'IBAN de remise', source: 'ini_config sepa_iban' },
      { field: 'BIC de remise', source: 'ini_config sepa_bic', note: 'Facultatif ; ignoré si invalide.' },
      { field: 'Nom / adresse du créancier', source: 'Informations générales V2 (lot Identité de l’organisation)' },
      { field: 'Configuration V2 déjà complète', source: '—', note: 'Jamais réécrite ; une configuration partielle est complétée.' },
    ],
  },
  'billing.sepa_mandates': {
    intro:
      "Mandats de prélèvement SEPA actifs des familles V1 vers les mandats créancier V2 (origine « papier », actifs immédiatement). La V2 exige une preuve de consentement : le mandat V1 est reconstitué en PDF avec la signature capturée à l'écran par le payeur.",
    rules: [
      { field: 'Payeur', source: 'sepa.parentsID → famille, studentID → élève payeur, third_partyID → tiers (records du lot Payeurs)' },
      { field: 'IBAN / BIC débiteur', source: 'sepa.sepa_iban / sepa_bic', note: 'IBAN invalide → exclu (nouveau mandat à faire signer depuis la V2).' },
      { field: 'Nom du débiteur', source: 'Payeur V2 (règle V2)', note: 'Le nom saisi en V1 est conservé en données cibles.' },
      { field: 'Schéma / séquence', source: 'CORE / récurrent (seul cas V1)' },
      { field: 'Date de signature', source: 'sepa.create_date', note: 'sepa_validate_date V1 (création + 36 mois) est informative : la V2 recalcule l’expiration CORE.' },
      { field: 'Preuve (PDF)', source: 'Mandat V1 reconstitué : RUM, ICS, créancier, texte légal, débiteur, IBAN, élèves, date + signature (sepa_signature, sinon uploads/images/sepa)' },
      { field: 'RUM', source: 'Générée par la V2', note: '⚠️ L’API V2 ne reprend pas la RUM V1 : conservée en données cibles ; le premier prélèvement V2 se présente à la banque sous une nouvelle référence.' },
      { field: 'Élèves couverts', source: 'Élèves rattachés au payeur V2 → contrats du mandat' },
      { field: 'Doublons', source: 'Un mandat vivant par payeur et par IBAN (règle V2)', note: 'Père + mère avec deux mandats actifs → le plus récent seul ; IBAN déjà mandaté pour un autre payeur → exclu avec la raison.' },
    ],
  },
  'billing.balance_invoices': {
    intro:
      "Facture V2 de SOLDE par facture V1 numérotée non entièrement réglée : le reste dû, adressé au payeur V2, en autant de mensualités qu'il reste d'échéances V1 impayées. Au premier record, le lot ferme la facturation V1 (création de factures, avoirs et paiements en ligne) si ce n'est pas déjà fait.",
    rules: [
      {
        field: 'Reste à payer',
        source: 'Σ lignes (montant − remise − remise fixe + TVA) − règlements (payment) − avoirs validés (credit) + pénalités − remises (weaverandfine)',
        note: 'Repris TEL QUEL sur une ligne hors TVA, article technique « Reprise de solde V1 » (code V1-SOLDE). Une facture V1 avec TVA est signalée.',
      },
      { field: 'Payeur', source: 'Foyer de l’élève (student.parentID), élève payeur ou tiers (thirdPartyId) → record du lot Payeurs' },
      {
        field: 'Mensualités',
        source: 'Échéances V1 non payées (statut ≠ 4, reste > 0)',
        note: 'Modèle d’échéancier « N échéances » : existant à N mensualités réutilisé, sinon créé TECHNIQUE (isVisible = false, masqué des directeurs). Plafond V2 : 12.',
      },
      { field: 'Échéances V2', source: 'Aperçu V2 du modèle à la date du jour (fin de mois)', note: 'La V2 ne génère jamais les échéances depuis le modèle : elles sont fournies à la création.' },
      { field: 'Numéro', source: 'Validation V2 → prochain numéro de la séquence (lot Numérotation)', note: 'Brouillon conservé si la validation échoue : la relance ne fait que valider.' },
      { field: 'Référence V1', source: 'Libellé de ligne « Solde facture V1 n° … du … », description (élève, année, TTC, réglé, avoirs, échéances V1) et notes' },
      { field: 'Exercice', source: 'Exercice V2 ouvert couvrant la date du jour' },
    ],
  },
};

/** Set dérivé pour le check rapide d'affichage du bouton côté `LotsTable`. */
export const LOTS_WITH_MAPPING_RULES = new Set<string>(Object.keys(LOT_MAPPING_RULES));

/**
 * Warnings affichés en clair sur la ligne d'un lot (icône d'alerte + survol).
 * Sert à rappeler des limites connues — typiquement des champs V1 sans
 * équivalent V2 (donc non migrés) tant que la V2 ne les implémente pas.
 *
 * Clé = `MigrationStepDefinition.code`.
 */
export const LOT_WARNINGS: Record<string, string[]> = {
  'billing.balance_invoices': [
    'Ce lot FERME la facturation V1 (création de factures, avoirs, remises SEPA, paiements en ligne) au premier record si ce n’est pas déjà fait — réversible depuis « Fermer la facturation V1 » sur le projet.',
    'Les factures de solde prennent les prochains numéros de la séquence : lancez (ou relancez) « Numérotation des documents » APRÈS la fermeture V1, avant ce lot.',
  ],
  'billing.numbering': [
    'À lancer ou relancer APRÈS la fermeture de la facturation V1 : le premier numéro V2 est figé au dernier compteur V1 émis + 1.',
  ],
  'billing.sepa_mandates': [
    'La V2 attribue une nouvelle RUM à chaque mandat repris (l’API ne reprend pas la RUM V1) : prévenez la banque ou les payeurs que la référence de mandat change ; la RUM V1 reste consultable dans les données cibles.',
    'Un mandat repris n’a jamais été utilisé côté V2 : le premier prélèvement partira en séquence FRST.',
  ],
};

/** Set dérivé pour le check rapide côté `LotsTable`. */
export const LOTS_WITH_WARNINGS = new Set<string>(Object.keys(LOT_WARNINGS));
